import { css } from '@emotion/core'
import { lighten } from 'polished'

import './fonts/typeface-inter'
import './fonts/typeface-silka'
const name = 'businessnewsdaily.com'
const SITE_NAME = 'bnd'

/**
 * Stole this Breakpoint setting logic from BuyerZone (@Alex + @Steve)
 */

// made BREAKPOINTS an object to easily use as a constant within main code
const BREAKPOINTS = {
	XS: 380,
	S: 569,
	M: 768,
	L: 992,
	XL: 1200,
	MAX: 1366,
	MMAX: 1800,
}

const LIST_BREAKPOINTS = Object.entries(BREAKPOINTS) // make breakpoints into list

// sample return size val => (min-width: 768px)
const MATCH_MEDIA = LIST_BREAKPOINTS.reduce(
	(acc, bp) => ({
		...acc,
		[bp[0]]: `(min-width: ${bp[1]}px)`,
	}),
	{}
)

// sample return size val => @media (min-width: 768px)
const MQ = LIST_BREAKPOINTS.reduce(
	(acc, bp) => ({
		...acc,
		[bp[0]]: `@media ${MATCH_MEDIA[bp[0]]}`,
	}),
	{}
)

/* MEDIA QUERY FOR BETWEEN BREAKPOINTS */
const BETWEEN_BREAKPOINTS = [
	['SM', 569, 768],
	['ML', 768, 992],
	['MXL', 768, 1200],
	['LXL', 992, 1200],
]

// sample return size val => (min-width: 576px) and (max-width: 768px)
const MATCH_BETWEEN_MEDIA = BETWEEN_BREAKPOINTS.reduce(
	(acc, bp) => ({
		...acc,
		[bp[0]]: `(min-width: ${bp[1]}px) and (max-width: ${bp[2]}px)`,
	}),
	{}
)

// sample return size val => @media (min-width: 768px)
const BMQ = BETWEEN_BREAKPOINTS.reduce(
	(acc, bp) => ({
		...acc,
		[bp[0]]: `@media ${MATCH_BETWEEN_MEDIA[bp[0]]}`,
	}),
	{}
)

/* HEADERS */
const HEADERS = [
	['h1', 55, 38],
	['h2', 30, 24],
	['h3', 22, 18],
]

const header = HEADERS.reduce(
	(acc, header) => ({
		...acc,
		[header[0]]: `
		font-size: ${header[2]}px;
		font-weight: 500;
		${MQ.M} {
			font-size: ${header[1]}px;
		}
	`,
	}),
	{}
)

/* Max Widths */

const MAXWIDTH = [
	['L', 1200], // Ultimate max for displaying text
]

const maxWidth = MAXWIDTH.reduce(
	(acc, max) => ({
		...acc,
		[max[0]]: `
		max-width: ${max[1]}px;
	`,
	}),
	{}
)

/* FONTS */
const fonts = {
	primary: css`
		font-family: 'Silka', 'sans-serif';
	`,
	//CTA
	secondary: css`
		font-family: 'Inter SemiBold', sans-serif;
	`,
	header: css`
		font-family: 'Silka Medium', 'sans-serif';
	`,
	primaryHeader: css`
		font-family: 'Inter ExtraBold', 'sans-serif';
	`,
	bold: css`
		font-family: 'Silka Bold', sans-serif;
		font-weight: 700;
	`,
	primarySemiBold: css`
		font-family: 'Silka SemiBold';
	`,
}

const colors = {
	black: '#000000',
	blue: '#ababab',
	blueGrey: '#D8DFE4',
	blueLagoon: '#005564',
	buttonText: '#FFFFFF',
	contentLink: '#087EE2',
	contentLinkHover: '#106488',
	darkBlue: '#00348D',
	darkGreen: '#008D64',
	darkGrey: '#505050', // old hex: #787878
	darkGreyOld: '#787879',
	dimGrey: '#6d6d6d',
	dolphin: '#6b6a7a',
	grey01: '#E1E1E1',
	grey00: '#E0E0E0',
	gray80: '#DEE1E4',
	green: '#A1D884',
	lightBlue: '#094463',
	lightBrown: '#d3d3d3',
	lightGrey: '#F2F5F7',
	lime: '#E0E721',
	navy: '#003B5C',
	orange: '#FFA06A',
	outlineBlue: '#0d8ed1',
	purple: '#44499C',
	red: '#ff4136',
	teal: '#00788D',
	darkTeal: '#00263B',
	text: '#49485B',
	washedNavy: '#768DA7',

	// THESE ARE BASED OFF OF NEW REVISED GREY COLOR SCHEME PROVIDED BY KEVIN
	white: '#ffffff',
	greyL1: '#F2F5F7',
	greyM1: '#919191',
	greyM2: '#505050',
	gray: {
		50: '#F2F5F7',
		100: '#FAFAFA',
		200: '#EEEEEE',
		300: '#D7D7D7',
		400: '#B0B0B0',
		500: '#7A7A7A',
		600: '#505050',
		700: '#2D2D2D',
		800: '#181818',
		900: '#000000',
	},

	// NEW THEMING STRUCTURE THAT WILL EVENTUALLY TAKE OVER THE ABOVE
	primary: '#1BA4DE',
	secondary: '#F2AD14',
	tertiary: '#106488',
	tertiaryLight: lighten(0.09, '#106488'),
	general: '#505050',

	backgroundRolloverTint: '#FAFAFA',
	borderLight: '#EEEEEE',
	borderMedium: '#D7D7D7',
	borderDark: '#B0B0B0',
	textLight: '#7A7A7A',
	textMedium: '#505050',
	textDark: '#2D2D2D',
	textExtraDark: '#181818',
}

const navigation = {
	linkColor: colors.tertiary,
	linkHoverColor: colors.primary,
	altLinkColor: colors.primary,
	altLinkHoverColor: colors.tertiary,
}

const navigationBar = {
	backgroundColor: colors.primary,
}

const backToTop = {
	mainColor: colors.primary,
	hoverColor: colors.tertiary,
}

const badges = {
	article: '#65ADCC',
	category: '#6592CC',
	editorsPick: colors.secondary,
	question: colors.tertiary,
	review: colors.tertiary,
}

const bestPicks = {
	badgeWrapperBackground: colors.gray[700],
	badgeWrapperFeaturedTextColor: colors.gray[700],
	bannerRibbonColor1: colors.secondary,
	bannerRibbonColor2: '#FBD427',
	bannerRibbonColor3: '#B29100',
	bannerRibbonColor4: '#FFD000',
	bulletTextColor: colors.gray[700],
	checkMark: colors.primary,
	chevronColor: colors.primary,
	companyNameColor: colors.tertiary,
	companyNameHoverColor: colors.primary,
	featuredTitleBackground: colors.gray[700],
	featuredTitleColor: colors.white,
	meatballColor: colors.primary,
	moreCoverageArticleBackground: colors.primary,
	moreCoverageTitleColor: colors.white,
	ourApproachBackground: css`
		background: linear-gradient(#1285b5, ${colors.primary});
	`,
	primarySvgColor: colors.white,
	secondarySvgColor: '#64D1FF',
	altSectionTitleColor: colors.primary,
	sectionTitleColor: colors.tertiary,
	serviceTitleColor: colors.gray[600],
}

const editorialUpdateSection = {
	mainColor: colors.primary,
}

const hubTemplate = {
	articleTitle: colors.gray[700],
	articleTitleHover: colors.primary,
	bestPickTitleColor: colors.gray[700],
	bestPickTitleHoverColor: colors.tertiary,
	editorsBlockTitle: colors.tertiary,
	hoverPlusArrowColor: colors.primary,
	linkColor: colors.primary,
	linkHoverColor: colors.tertiary,
	top3BestPickBackground: 'rgba(16,100,136,0.9)',
}

const simpleHeader = {
	breadcrumbsBackground: colors.gray[200],
	authorByLineColor: colors.gray[400],
	hubTitleColor: colors.white,
	mbpTitleColor: colors.gray[700],
	fontFamily: fonts.primaryHeader,
	hubGradientBgLight: colors.primary,
	hubGradientBgDark: colors.tertiary,
	mbpGradientBgLight: colors.gray[100],
	mbpGradientBgDark: colors.gray[200],
}

const search = {
	borderColor: colors.primary,
	strokeColor: colors.tertiary,
}

const footer = {
	chevronColor: colors.tertiary,
}

// NEW THEMING STRUCTURE THAT WILL EVENTUALLY TAKE OVER BUTTONS
const button = {
	isButtonRounded: true,
	fontFamily: 'Silka SemiBold',
	primary: {
		buttonColor: colors.primary,
		buttonText: colors.white,
		hoverColor: colors.tertiary,
	},
	secondary: {
		buttonColor: colors.white,
		buttonText: '#3F3F3F',
		borderColor: colors.primary,
		hoverColor: '#64D1FF',
		hoverBorderColor: colors.primary,
		hoverTextColor: colors.white,
	},
}

const arrowActionCard = {
	arrowColor: colors.white,
	topBannerBackground: colors.primary,
	topBannerHover: colors.tertiary,
}

const authorBio = {
	staffName: colors.black,
	staffSite: colors.gray[400],
	staffProfileLink: colors.primary,
	staffProfileLinkHover: colors.tertiary,
	altStaffProfileLinkHover: colors.tertiary,
}

const contentHeader = {
	authorBylineFont: fonts.header,
	authorBylineBorder: colors.primary,
}

const emailCapture = {
	buttonColor: colors.primary,
	floaterBackground: '#007ecc',
	inlineButtonHover: colors.tertiary,
	labelColor: colors.tertiary,
	policyLink: colors.secondary,
	policyLinkHover: colors.primary,
}

const relatedRail = {
	backgroundHoverColor: colors.backgroundRolloverTint,
	headerFontFamily: fonts.primarySemiBold,
	linkColor: colors.primary,
	linkHoverColor: colors.tertiary,
	textColor: colors.textMedium,
	textHoverColor: colors.textExtraDark,
}

const reviews = {
	border: colors.primary,
	categoryLink: colors.primary,
	categoryHoverLink: colors.tertiary,
}

const sectionTitle = {
	textColor: colors.gray[600],
}

const socialProfileSvgs = {
	primary: {
		backgroundColor: 'none',
		fillColor: colors.primary,
		hoverBackgroundColor: 'none',
		hoverFillColor: colors.tertiary,
	},
	neutral: {
		backgroundColor: colors.gray[400],
		fillColor: colors.white,
		hoverBackgroundColor: colors.primary,
		hoverFillColor: colors.white,
	},
	inverse: {
		backgroundColor: 'none',
		fillColor: colors.tertiary,
		hoverBackgroundColor: 'none',
		hoverFillColor: colors.white,
	},
}

const tableOfContents = {
	bulletBorderColor: colors.tertiary,
	hoverColor: colors.primary,
	lineColor: colors.tertiary,
}

const background = {
	default: {
		backgroundColor: colors.white,
	},
	secondary: {
		backgroundColor: 'rgba(0, 0, 0, 0.12)',
	},
}

const variants = {
	default: {
		backgroudColor: colors.secondary,
		backgroudColorHover: colors.tertiary,
	},
	secondary: {
		backgroudColor: colors.primary,
		backgroudColorHover: colors.secondary,
	},
}

const shadows = {
	small: '0px 0px 2px rgba(0, 0, 0, 0.16)',
	medium: '0 2px 30px 0 rgba(0, 0, 0, 0.16)',
	large: '0px 3px 3px rgba(0, 0, 0, 0.3)',
	greyMedium: '0px 2px 14px 0px rgba(139, 142, 145, 0.27)',
}

const spacing = {
	default: '20px',
}

const subHubMenu = {
	buttonVariant: 'secondary',
	linkVariant: 'authorLink',
	trendingBackground: colors.primary,
	trendingEvenBackground: colors.gray[700],
	trendingOddBackground: colors.gray[800],
}

/* STYLED TEXT */
const text = {
	breadcrumb: css`
		color: ${colors.white};

		${MQ.M} {
			color: ${colors.dolphin};
		}
		a {
			&:hover {
				color: ${colors.tertiary};
			}
		}
	`,
	bodyLarge: css`
		font-size: 18px;
	`,
	bodySmall: css`
		font-size: 14px;

		${MQ.L} {
			font-size: 16px;
		}
	`,
	display1: css`
		font-family: Silka;
		font-size: 25px;
		font-weight: 500;
		font-style: normal;
		font-stretch: normal;
		line-height: normal;
		letter-spacing: 0.6px;
		color: ${colors.white};
		margin: 30px 0 0 0;
		height: 42px;
	`,
	display2: css`
		padding-top: 48px;
		margin: 0 48px;
		font-family: Silka;
		font-size: 1.2rem;
		font-weight: 500;
		font-style: normal;
		font-stretch: normal;
		line-height: normal;
		letter-spacing: 0.4px;
	`,
	menuItem: css`
		color: ${colors.primary};
		font-size: 20px;
	`,
	subMenuItem: css`
		color: ${colors.primary};
		font-size: 16px;
	`,
	sectionHeaderLarge: css`
		color: ${colors.tertiary};
		font-size: 16px;
		font-weight: 500;
		${fonts.secondary};
		letter-spacing: 3px;
		text-transform: uppercase;

		${MQ.M} {
			font-size: 22px;
		}
	`,
	sectionHeaderSmall: css`
		color: ${colors.tertiary};
		font-size: 14px;
		font-weight: 500;
		${fonts.secondary};
		letter-spacing: 3px;
		text-transform: uppercase;
	`,
}

const borders = {
	default: `1px solid ${colors.grey01}`,
	secondary: `1px solid rgba(109, 109, 109, 0.2)`,
}

/* CTA LINKS */
const baseCta = css`
	cursor: pointer;
	font-size: 14px;
	font-weight: 700;
	letter-spacing: 3px;
`

const ctas = {
	default: css`
        ${baseCta}
        color: ${colors.primary};
        &:hover {
            color: ${colors.tertiary};
        }
	`,
	white: css`
		${baseCta}
		color: ${colors.white};
		&:hover {
			color: ${colors.secondary};
		}
	`,
	navy: css`
        ${baseCta}
        color: ${colors.primary};
        &:hover {
            color: ${colors.secondary};
        }
	`,
	teal: css`
		${baseCta}
		color: ${colors.tertiary};
		&:hover {
			color: ${colors.secondary};
		}
	`,
}

/* LINKS */
const baseLink = css`
	cursor: pointer;
`

const links = {
	default: css`
        ${baseLink}
        color: ${colors.primary};
        &:hover {
            color: ${colors.tertiary};
        }
    `,
	white: css`
		${baseLink}
		color: ${colors.white};
		&:hover {
			color: ${colors.secondary};
		}
	`,
	navy: css`
        ${baseLink}
        color: ${colors.primary};
        &:hover {
            color: ${colors.secondary};
        }
	`,
	teal: css`
		${baseLink}
		color: ${colors.tertiary};
		&:hover {
			color: ${colors.secondary};
		}
	`,
	darkGrey: css`
		${baseLink}
		color: ${colors.darkGrey};
		&:hover {
			color: ${colors.secondary};
		}
	`,
}

const shareBaseButton = css`
	border: none;
	display: block;
	cursor: pointer;
`

/* BUTTONS */
const baseButton = css`
	${shareBaseButton}
	font-size: 14px;
	font-weight: 800;
	letter-spacing: 3px;
	max-width: 290px;
	padding: 20px 0;
	text-align: center;
	text-transform: uppercase;
	transition: all 0.4s ease;
	width: 100%;
	${MQ.L} {
		max-width: 288px;
		padding: 21px 0;
	}

	&:disabled {
		background-color: ${colors.gray80};
		cursor: not-allowed;
		transition: none;
	}
`

const smallBaseButton = css`
	${shareBaseButton}
	${fonts.secondary};
	font-size: 12px;
	font-weight: 800;
	letter-spacing: 2px;
	max-width: fit-content;
	padding: 13px 10px;
	text-align: center;
	text-transform: uppercase;
	transition: all 0.4s ease;
	width: 100%;
`

/* BUTTONS */
const buttons = {
	default: css`
		${baseButton}
		background: ${colors.primary};
		color: ${colors.buttonText};
		${fonts.secondary};
		&:hover:not([disabled])  {
			background: ${colors.tertiary};
			color: ${colors.white};
		}
		&:active {
			background-color: ${colors.blueLagoon};
		}
	`,
	secondary: css`
		${baseButton}
		background: ${colors.green};
		color: ${colors.buttonText};
		${fonts.secondary};
		&:hover:not([disabled]) {
			background: ${colors.tertiary};
			color: ${colors.white};
		}
	`,
	transparent: css`
		${baseButton}
		outline: solid 1px ${colors.blueGrey};
		color: ${colors.black};
		&:hover:not([disabled]) {
			background-color: ${colors.primary};
			color: ${colors.white};
		}
		&:active {
			background-color: ${colors.blueLagoon};
		}
	`,
	link: css`
		${baseButton}
		${baseCta}
		background: transparent;
		color: ${colors.secondary};
		${fonts.secondary};
		font-weight: 600;
		font-size: 1rem;
		text-transform: none;
		width: 80%;
	`,
	small: css`
		${smallBaseButton}
		background: ${colors.secondary};
		color: ${colors.buttonText};
		${fonts.secondary};
		&:hover {
			background: ${colors.tertiary};
			color: ${colors.white};
		}
	`,
	smallSecondary: css`
		${smallBaseButton}
		background: ${colors.green};
		color: ${colors.buttonText};
		width: auto;
		padding: 20px 15px;
		&:hover {
			background: ${colors.primary};
			color: ${colors.white};
		}
	`,
}

const triangle = css`
	width: 0;
	height: 0;
	border-left: 7px solid transparent;
	border-right: 7px solid transparent;
	border-bottom: 11px solid ${colors.primary};
`
export const bndTheme = {
	arrowActionCard,
	authorBio,
	background,
	backToTop,
	badges,
	bestPicks,
	BMQ,
	borders,
	BREAKPOINTS,
	button,
	buttons,
	colors,
	contentHeader,
	ctas,
	editorialUpdateSection,
	emailCapture,
	fonts,
	footer,
	header,
	hubTemplate,
	links,
	MATCH_BETWEEN_MEDIA,
	MATCH_MEDIA,
	maxWidth,
	MQ,
	name,
	navigation,
	navigationBar,
	relatedRail,
	reviews,
	search,
	sectionTitle,
	shadows,
	simpleHeader,
	SITE_NAME,
	socialProfileSvgs,
	spacing,
	subHubMenu,
	tableOfContents,
	text,
	triangle,
	variants,
}
